import { useQuery } from '@tanstack/react-query';
import { useAuth } from './use-auth';
import { UserService, UserDto, UserActivityDto, UserMatchIdentifierListResponse } from '../services/user-service';
import httpClient from '../services/http-client';

const userService = new UserService(httpClient);

/**
 * Hook to get current user's profile information
 */
export function useCurrentUserProfile() {
  const { isAuthenticated, tokenClaims } = useAuth();
  const userId = tokenClaims?.sub;

  return useQuery({
    queryKey: ['user-profile', userId],
    queryFn: async (): Promise<UserDto | null> => {
      if (!userId) return null;
      try {
        return await userService.getUserById(userId);
      } catch (error) {
        console.error('Error fetching user profile:', error);
        return null;
      }
    },
    enabled: isAuthenticated && !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });
}

/**
 * Hook to get current user's activity
 */
export function useCurrentUserActivity(startDate?: string, endDate?: string) {
  const { isAuthenticated, tokenClaims } = useAuth();
  const userId = tokenClaims?.sub;

  return useQuery({
    queryKey: ['user-activity', userId, startDate, endDate],
    queryFn: async (): Promise<UserActivityDto | null> => {
      if (!userId) return null;
      try {
        return await userService.getUserActivity(userId, startDate, endDate);
      } catch (error) {
        console.error('Error fetching user activity:', error);
        return null;
      }
    },
    enabled: isAuthenticated && !!userId,
    staleTime: 30 * 1000, // 30 seconds
    retry: false,
  });
}

/**
 * Hook to get user's match identifiers for a specific division
 */
export function useUserMatchIdentifiers(divisionId: string | undefined, userId: string | undefined) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['user-match-identifiers', divisionId, userId],
    queryFn: async (): Promise<UserMatchIdentifierListResponse | null> => {
      if (!divisionId || !userId) return null;
      try {
        return await userService.getUserMatchIdentifiers(divisionId, userId);
      } catch (error) {
        console.error('Error fetching user match identifiers:', error);
        return null;
      }
    },
    enabled: isAuthenticated && !!divisionId && !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: false,
  });
}

/**
 * Hook to get a specific user's profile (for admin use)
 */
export function useUserProfile(userId: string) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['user-profile', userId],
    queryFn: async (): Promise<UserDto> => {
      return await userService.getUserById(userId);
    },
    enabled: isAuthenticated && !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
